import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UtensilsCrossed, Edit2, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface MealPlanCardProps {
  records: any[];
  onRecordsChange: (updatedRecords: any[]) => void;
}

export function MealPlanCard({ records, onRecordsChange }: MealPlanCardProps) {
  const mealPlanRecords = records.filter(r => r.type === 'meal_plan');
  const [editingMeal, setEditingMeal] = useState<{
    recordIdx: number;
    mealIdx: number;
  } | null>(null);
  const [editForm, setEditForm] = useState<{
    foods: string;
    calories: number | null;
  }>({
    foods: '',
    calories: null
  });

  if (mealPlanRecords.length === 0) return null;

  const startEditingMeal = (recordIdx: number, mealIdx: number) => {
    const record = mealPlanRecords[recordIdx];
    const meal = record.meals[mealIdx];

    setEditingMeal({ recordIdx, mealIdx });
    setEditForm({
      foods: Array.isArray(meal.foods) ? meal.foods.join(', ') : meal.foods || '',
      calories: meal.calories || null
    });
  };

  const cancelEditingMeal = () => {
    setEditingMeal(null);
    setEditForm({ foods: '', calories: null });
  };

  const saveMeal = () => {
    if (!editingMeal) return;

    const { recordIdx, mealIdx } = editingMeal;
    const updatedRecords = [...records];
    const recordIndex = records.indexOf(mealPlanRecords[recordIdx]);
    const updatedRecord = { ...updatedRecords[recordIndex] };
    const updatedMeals = [...updatedRecord.meals];

    updatedMeals[mealIdx] = {
      ...updatedMeals[mealIdx],
      foods: editForm.foods.split(',').map(f => f.trim()).filter(Boolean),
      calories: editForm.calories
    };

    updatedRecord.meals = updatedMeals;
    updatedRecords[recordIndex] = updatedRecord;

    onRecordsChange(updatedRecords);
    setEditingMeal(null);
    toast.success("Refeição atualizada!");
  };

  const deleteMeal = (recordIdx: number, mealIdx: number) => {
    const updatedRecords = [...records];
    const recordIndex = records.indexOf(mealPlanRecords[recordIdx]);
    const updatedRecord = { ...updatedRecords[recordIndex] };
    const updatedMeals = updatedRecord.meals.filter((_: any, idx: number) => idx !== mealIdx);

    updatedRecord.meals = updatedMeals;
    updatedRecords[recordIndex] = updatedRecord;

    onRecordsChange(updatedRecords);
    toast.success("Refeição excluída!");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <UtensilsCrossed className="h-5 w-5 text-orange-600" />
        <h3 className="text-lg font-semibold text-orange-600">Plano Alimentar</h3>
        <Badge variant="secondary">{mealPlanRecords.length} plano(s)</Badge>
      </div>

      {mealPlanRecords.map((record, idx) => (
        <Card key={idx} className="border-l-4 border-l-orange-600">
          <CardContent className="pt-4">
            <div className="space-y-3">
              {/* Nome do plano */}
              {record.plan_name && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Plano:</span>
                  <p className="text-base font-semibold">{record.plan_name}</p>
                </div>
              )}

              {/* Data de início */}
              {record.start_date && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Início:</span>
                  <p className="text-sm">{new Date(record.start_date).toLocaleDateString('pt-BR')}</p>
                </div>
              )}

              {/* Tabela de refeições */}
              {Array.isArray(record.meals) && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Dia</th>
                        <th className="text-left p-2">Refeição</th>
                        <th className="text-left p-2">Alimentos</th>
                        <th className="text-right p-2">Cal.</th>
                        <th className="w-24"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {record.meals.map((meal: any, mealIdx: number) => {
                        const isEditing = editingMeal?.recordIdx === idx && editingMeal?.mealIdx === mealIdx;

                        return (
                          <tr key={mealIdx} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-medium">{meal.day}</td>
                            <td className="p-2">{meal.meal_type}</td>
                            <td className="p-2">
                              {isEditing ? (
                                <Input
                                  value={editForm.foods}
                                  onChange={(e) => setEditForm({ ...editForm, foods: e.target.value })}
                                  className="h-8"
                                  placeholder="Alimentos (separados por vírgula)"
                                />
                              ) : (
                                Array.isArray(meal.foods)
                                  ? meal.foods.join(', ')
                                  : meal.foods
                              )}
                            </td>
                            <td className="p-2 text-right">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={editForm.calories || ''}
                                  onChange={(e) => setEditForm({ ...editForm, calories: e.target.value ? parseInt(e.target.value) : null })}
                                  className="h-8 w-20 text-right"
                                  placeholder="0"
                                />
                              ) : (
                                meal.calories ? `${meal.calories} kcal` : '-'
                              )}
                            </td>
                            <td className="p-2">
                              {isEditing ? (
                                <div className="flex items-center gap-1 justify-end">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={saveMeal}
                                    className="h-7 w-7 p-0"
                                  >
                                    <Check className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={cancelEditingMeal}
                                    className="h-7 w-7 p-0"
                                  >
                                    <X className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 justify-end">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => startEditingMeal(idx, mealIdx)}
                                    className="h-7 w-7 p-0"
                                  >
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteMeal(idx, mealIdx)}
                                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Notas */}
              {record.notes && (
                <div className="bg-muted/50 p-3 rounded-md">
                  <span className="text-sm font-medium text-muted-foreground">Orientações:</span>
                  <p className="text-sm mt-1">{record.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
